import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  Video,
  Copy,
  MoreVertical,
  Archive,
  Trash2,
  Pencil,
  Clock,
  Users,
  Search,
  X as XIcon,
  ArchiveRestore,
  PhoneOff,
  Link2,
  Settings,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { formatInTz, userBrowserTimezone } from "@/lib/time";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import MeetingLinks from "@/pages/MeetingLinks";
import MeetingSettingsModal from "@/components/MeetingSettingsModal";

const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "scheduled", label: "Scheduled" },
  { value: "live", label: "Live" },
  { value: "ended", label: "Ended" },
];

export default function Meetings() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [tab, setTab] = useState("all");
  // Top-level tab: regular one-off meetings (default) vs persistent
  // "Meeting Links" (evergreen rooms).
  const [topTab, setTopTab] = useState("meetings");
  const [loading, setLoading] = useState(true);
  // archive view: 'active' (default) | 'archived'
  const [archiveView, setArchiveView] = useState("active");
  const [search, setSearch] = useState("");
  // selection state for bulk actions
  const [selected, setSelected] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState(null); // 'archive' | 'delete' | null
  const [bulkBusy, setBulkBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();

  const fetchMeetings = async () => {
    if (!currentWorkspace) return;
    setLoading(true);
    try {
      const params = {
        workspace_id: currentWorkspace.workspace_id,
        // Keep evergreen rooms out of the one-off meeting feed — they
        // live in the "Meeting Links" tab.
        exclude_persistent: true,
      };
      if (archiveView === "archived") params.archived_only = true;
      const { data } = await api.get(`/meetings`, { params });
      setMeetings(data);
      setSelected(new Set()); // clear selection on reload
    } catch {
      toast.error("Could not load meetings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace, archiveView]);

  // tab + search filtering, plus "soonest to latest" default sort.
  // Sort spec:
  //   - Upcoming/in-progress meetings (status !== "ended") at the top,
  //     ascending by start_time (next-up first → farther-out last).
  //   - Past meetings (status === "ended") at the bottom, descending by
  //     actual_end → actual_start → start_time → created_at (most-recently-
  //     ended first), preserving the existing "Ended" tab feel.
  //   - In the "Ended" tab the upcoming bucket is empty so the past-desc
  //     ordering becomes the whole list — identical to the prior server
  //     behaviour. In the "Scheduled" / "Live" tabs the past bucket is empty
  //     so only the ascending order applies.
  const filtered = useMemo(() => {
    let rows = tab === "all" ? meetings : meetings.filter((m) => m.status === tab);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((m) => {
        const fields = [
          m.title || "",
          m.description || "",
          ...(m.invited_emails || []),
          m.status || "",
        ];
        return fields.some((f) => String(f).toLowerCase().includes(q));
      });
    }
    const isPast = (m) => m.status === "ended";
    const bestPastTime = (m) =>
      m.actual_end || m.actual_start || m.start_time || m.created_at || "";
    const bestUpcomingTime = (m) => m.start_time || m.created_at || "";
    return [...rows].sort((a, b) => {
      const pa = isPast(a);
      const pb = isPast(b);
      if (pa !== pb) return pa ? 1 : -1; // upcoming first, past after
      if (pa) {
        // both past — descending (most-recently-ended first)
        return bestPastTime(b).localeCompare(bestPastTime(a));
      }
      // both upcoming — ascending (soonest first)
      return bestUpcomingTime(a).localeCompare(bestUpcomingTime(b));
    });
  }, [meetings, tab, search]);

  const allVisibleSelected =
    filtered.length > 0 && filtered.every((m) => selected.has(m.meeting_id));
  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((m) => m.meeting_id)));
    }
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action) => {
    const ids = Array.from(selected);
    if (!ids.length || !currentWorkspace) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/meetings/bulk`, {
        workspace_id: currentWorkspace.workspace_id,
        meeting_ids: ids,
        action,
      });
      const verbs = {
        archive: "archived",
        delete: "deleted",
        restore: "restored",
        unarchive: "restored",
      };
      toast.success(`${data.modified || ids.length} meeting${ids.length === 1 ? "" : "s"} ${verbs[action] || action + "d"}`);
      setBulkAction(null);
      fetchMeetings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Bulk ${action} failed`);
    } finally {
      setBulkBusy(false);
    }
  };

  const copyLink = (m) => {
    const url = `${window.location.origin}/m/${m.public_id}`;
    navigator.clipboard.writeText(url);
    toast.success("Meeting link copied");
  };

  const archive = async (m) => {
    await api.patch(`/meetings/${m.meeting_id}`, { is_archived: true });
    toast.success("Meeting archived");
    fetchMeetings();
  };
  const unarchive = async (m) => {
    await api.patch(`/meetings/${m.meeting_id}`, { is_archived: false });
    toast.success("Meeting restored");
    fetchMeetings();
  };
  const cancelMeeting = async (m) => {
    await api.patch(`/meetings/${m.meeting_id}`, { status: "canceled" });
    toast.success("Meeting canceled");
    fetchMeetings();
  };
  const endMeeting = async (m) => {
    // Same status flip the in-meeting "End for all" host control uses.
    // Lets hosts close out a meeting card that's stuck on "Live" because
    // the host disconnected without ending it.
    if (!window.confirm(`End "${m.title}" for everyone? This closes the live room.`)) return;
    try {
      await api.patch(`/meetings/${m.meeting_id}`, { status: "ended" });
      // Best-effort broadcast so anyone still on the page gets routed out.
      try { await api.post(`/meetings/${m.meeting_id}/host/end-for-all`, {}); } catch {}
      toast.success("Meeting ended");
      fetchMeetings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not end meeting");
    }
  };
  const removeMeeting = async (m) => {
    if (!window.confirm(`Delete "${m.title}" permanently? This cannot be undone.`)) return;
    await api.delete(`/meetings/${m.meeting_id}`);
    toast.success("Meeting deleted");
    fetchMeetings();
  };

  const createInstant = async () => {
    if (!currentWorkspace) return;
    try {
      const { data } = await api.post("/meetings", {
        title: "Instant meeting",
        workspace_id: currentWorkspace.workspace_id,
        duration_minutes: 60,
        timezone: user?.timezone || userBrowserTimezone(),
        guest_access_enabled: currentWorkspace?.instant_meeting_guest_access_default ?? true,
        waiting_room_enabled: currentWorkspace?.instant_meeting_waiting_room_default ?? false,
        ai_summary_enabled: currentWorkspace?.instant_meeting_ai_summary_default ?? true,
        auto_summary_on_end: currentWorkspace?.instant_meeting_auto_summary_on_end_default ?? true,
      });
      navigate(`/m/${data.public_id}`);
    } catch {
      toast.error("Could not start instant meeting");
    }
  };

  const selectionCount = selected.size;

  return (
    <div className="flex flex-col gap-7" data-testid="meetings-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="bloq-eyebrow">Meetings</p>
          <h1 className="bloq-headline text-4xl sm:text-5xl mt-2">
            Every room you've run — past, present, scheduled.
          </h1>
        </div>
        {topTab === "meetings" && (
          <div className="flex gap-2">
            <button
              onClick={() => setSettingsOpen(true)}
              className="bloq-btn-secondary inline-flex items-center gap-2"
              data-testid="meeting-settings-button"
              title="Meeting defaults"
            >
              <Settings className="w-4 h-4" /> Meeting settings
            </button>
            <button
              onClick={createInstant}
              className="bloq-btn-secondary inline-flex items-center gap-2"
              data-testid="start-instant-meeting-button"
            >
              <Video className="w-4 h-4" /> Start instant meeting
            </button>
            <Link to="/app/meetings/new" className="bloq-btn-primary inline-flex items-center gap-2" data-testid="new-meeting-button">
              <Plus className="w-4 h-4" /> Schedule meeting
            </Link>
          </div>
        )}
      </header>

      <MeetingSettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />

      {/* Top-level tab: one-off Meetings vs evergreen Meeting Links. */}
      <div
        className="inline-flex border border-[#e5e0d6] text-sm w-fit"
        data-testid="meetings-top-tabs"
      >
        <button
          onClick={() => setTopTab("meetings")}
          className={`px-4 py-2 inline-flex items-center gap-2 ${topTab === "meetings" ? "bg-[#005e7a] text-white" : "bg-white text-[#4a4a52] hover:bg-[#f5efe6]"}`}
          data-testid="meetings-top-tab-meetings"
        >
          <Video className="w-4 h-4" /> Meetings
        </button>
        <button
          onClick={() => setTopTab("links")}
          className={`px-4 py-2 inline-flex items-center gap-2 border-l border-[#e5e0d6] ${topTab === "links" ? "bg-[#005e7a] text-white" : "bg-white text-[#4a4a52] hover:bg-[#f5efe6]"}`}
          data-testid="meetings-top-tab-links"
        >
          <Link2 className="w-4 h-4" /> Meeting Links
        </button>
      </div>

      {topTab === "links" ? (
        <MeetingLinks />
      ) : (
      <>
      {/* Search + archive view toggle */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-[#82828c] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search meetings by title, description, or invitee…"
            className="bloq-input pl-9 pr-9 w-full"
            data-testid="meetings-search-input"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[#f5efe6]"
              data-testid="meetings-search-clear"
              aria-label="Clear search"
            >
              <XIcon className="w-3.5 h-3.5 text-[#82828c]" />
            </button>
          )}
        </div>
        <div className="inline-flex border border-[#e5e0d6] overflow-hidden text-xs" data-testid="meetings-archive-toggle">
          <button
            onClick={() => setArchiveView("active")}
            className={`px-3 py-1.5 ${archiveView === "active" ? "bg-[#005e7a] text-white" : "bg-white text-[#4a4a52] hover:bg-[#f5efe6]"}`}
            data-testid="meetings-view-active"
          >
            Active
          </button>
          <button
            onClick={() => setArchiveView("archived")}
            className={`px-3 py-1.5 border-l border-[#e5e0d6] ${archiveView === "archived" ? "bg-[#005e7a] text-white" : "bg-white text-[#4a4a52] hover:bg-[#f5efe6]"}`}
            data-testid="meetings-view-archived"
          >
            Archived
          </button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <TabsList>
            {STATUS_TABS.map((s) => (
              <TabsTrigger key={s.value} value={s.value} data-testid={`tab-${s.value}`}>{s.label}</TabsTrigger>
            ))}
          </TabsList>
          {filtered.length > 0 && (
            <label className="flex items-center gap-2 text-xs text-[#4a4a52] cursor-pointer" data-testid="meetings-select-all-wrap">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleSelectAll}
                className="accent-[#005e7a] w-4 h-4"
                data-testid="meetings-select-all"
              />
              Select all visible
            </label>
          )}
        </div>

        {/* Bulk action bar — visible only when selection is non-empty */}
        {selectionCount > 0 && (
          <div
            className="mt-3 flex items-center justify-between gap-3 px-4 py-2 bg-[#f5efe6] border border-[#e5e0d6]"
            data-testid="meetings-bulk-bar"
          >
            <p className="text-sm text-[#1a1a1f]" data-testid="meetings-selected-count">
              {selectionCount} selected
            </p>
            <div className="flex flex-wrap gap-2">
              {archiveView === "archived" ? (
                <button
                  onClick={() => runBulk("unarchive")}
                  disabled={bulkBusy}
                  className="bloq-btn-secondary text-xs inline-flex items-center gap-1"
                  data-testid="meetings-bulk-unarchive"
                >
                  <ArchiveRestore className="w-3.5 h-3.5" /> Restore
                </button>
              ) : (
                <button
                  onClick={() => setBulkAction("archive")}
                  disabled={bulkBusy}
                  className="bloq-btn-secondary text-xs inline-flex items-center gap-1"
                  data-testid="meetings-bulk-archive"
                >
                  <Archive className="w-3.5 h-3.5" /> Archive
                </button>
              )}
              <button
                onClick={() => setBulkAction("delete")}
                disabled={bulkBusy}
                className="bloq-btn-secondary text-xs inline-flex items-center gap-1 text-[#c43838]"
                data-testid="meetings-bulk-delete"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
              <button
                onClick={clearSelection}
                className="bloq-btn-secondary text-xs"
                data-testid="meetings-bulk-clear"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        <TabsContent value={tab} className="mt-5">
          {loading ? (
            <p className="text-sm text-[#82828c]">Loading meetings…</p>
          ) : filtered.length === 0 ? (
            <div className="bloq-card p-10 text-center">
              <Video className="w-10 h-10 mx-auto text-[#82828c] mb-3" />
              <p className="bloq-headline text-xl mb-1 text-[#005e7a]">
                {search ? "No matching meetings" : archiveView === "archived" ? "No archived meetings" : "Nothing here yet"}
              </p>
              <p className="text-sm text-[#82828c] mb-5">
                {search
                  ? `No meetings match "${search}".`
                  : archiveView === "archived"
                  ? "Archived meetings show up here."
                  : tab === "scheduled"
                  ? "Schedule your first meeting to see it here."
                  : "Meetings will show up here as soon as you run them."}
              </p>
              {!search && archiveView === "active" && (
                <Link to="/app/meetings/new" className="bloq-btn-primary inline-flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Create your first meeting
                </Link>
              )}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map((m) => (
                <MeetingCard
                  key={m.meeting_id}
                  m={m}
                  userTz={user?.timezone || userBrowserTimezone()}
                  selected={selected.has(m.meeting_id)}
                  onToggleSelect={() => toggleOne(m.meeting_id)}
                  onCopy={() => copyLink(m)}
                  onArchive={m.is_archived ? () => unarchive(m) : () => archive(m)}
                  onCancel={() => cancelMeeting(m)}
                  onEnd={() => endMeeting(m)}
                  onDelete={() => removeMeeting(m)}
                  onOpen={() => navigate(`/app/meetings/${m.meeting_id}`)}
                  onJoin={() => navigate(`/m/${m.public_id}`)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Bulk confirmation modal */}
      <Dialog open={bulkAction !== null} onOpenChange={(o) => !o && setBulkAction(null)}>
        <DialogContent data-testid="meetings-bulk-confirm">
          <DialogHeader>
            <DialogTitle>
              {bulkAction === "archive" ? "Archive selected meetings?" : "Delete selected meetings?"}
            </DialogTitle>
            <DialogDescription>
              {bulkAction === "archive"
                ? "Archive selected meetings? You can still find archived meetings later."
                : "Delete selected meetings? This will remove them from your active meeting list. Related records may be retained depending on your workspace settings."}
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs text-[#82828c]">
            {selectionCount} meeting{selectionCount === 1 ? "" : "s"} selected.
          </p>
          <DialogFooter>
            <button
              onClick={() => setBulkAction(null)}
              className="bloq-btn-secondary"
              data-testid="meetings-bulk-cancel"
              disabled={bulkBusy}
            >
              Cancel
            </button>
            <button
              onClick={() => runBulk(bulkAction)}
              className={bulkAction === "delete" ? "bloq-btn-primary bg-[#c43838] hover:bg-[#a82929]" : "bloq-btn-primary"}
              data-testid="meetings-bulk-confirm-action"
              disabled={bulkBusy}
            >
              {bulkBusy ? "Working…" : bulkAction === "archive" ? "Archive" : "Delete"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    scheduled: { bg: "rgba(0,94,122,0.1)", c: "#005e7a", label: "Scheduled" },
    live: { bg: "rgba(227,74,74,0.1)", c: "#c43838", label: "Live" },
    ended: { bg: "rgba(74,74,82,0.08)", c: "#4a4a52", label: "Ended" },
    canceled: { bg: "rgba(74,74,82,0.08)", c: "#82828c", label: "Canceled" },
  };
  const s = map[status] || map.scheduled;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-widest"
      style={{ background: s.bg, color: s.c }}
    >
      {status === "live" && <span className="bloq-live-dot w-1.5 h-1.5" />}
      {s.label}
    </span>
  );
}

function MeetingCard({ m, userTz, selected, onToggleSelect, onCopy, onArchive, onCancel, onEnd, onDelete, onOpen, onJoin }) {
  return (
    <div
      className={`bloq-card p-5 flex flex-col gap-3 ${selected ? "ring-2 ring-[#005e7a]" : ""}`}
      data-testid={`meeting-card-${m.meeting_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!selected}
            onChange={onToggleSelect}
            onClick={(e) => e.stopPropagation()}
            className="accent-[#005e7a] w-4 h-4"
            data-testid={`meeting-select-${m.meeting_id}`}
            aria-label={`Select ${m.title}`}
          />
          <StatusBadge status={m.status} />
          {m.booking_id && (
            <span
              className="bloq-pill text-[10px] uppercase tracking-wider font-semibold bg-[#fef3c7] text-[#92400e]"
              data-testid={`meeting-card-booking-badge-${m.meeting_id}`}
              title="Auto-created from a booking on the Bookings page"
            >
              Booking
            </span>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger className="p-1 rounded hover:bg-[#f5efe6]" data-testid={`meeting-menu-${m.meeting_id}`}>
            <MoreVertical className="w-4 h-4 text-[#82828c]" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onOpen}><Pencil className="w-4 h-4 mr-2" /> Open details</DropdownMenuItem>
            <DropdownMenuItem onClick={onCopy}><Copy className="w-4 h-4 mr-2" /> Copy link</DropdownMenuItem>
            {m.status !== "canceled" && m.status !== "ended" && (
              <DropdownMenuItem onClick={onCancel}><Clock className="w-4 h-4 mr-2" /> Cancel</DropdownMenuItem>
            )}
            {m.status !== "ended" && m.status !== "canceled" && (
              <DropdownMenuItem onClick={onEnd} data-testid={`end-meeting-${m.meeting_id}`}>
                <PhoneOff className="w-4 h-4 mr-2" /> End meeting
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onArchive}><Archive className="w-4 h-4 mr-2" /> {m.is_archived ? "Restore" : "Archive"}</DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-red-600 focus:text-red-700">
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <button onClick={onOpen} className="text-left">
        <p className="bloq-headline text-lg leading-tight text-[#005e7a]">{m.title}</p>
        <p className="text-sm text-[#82828c] mt-1.5">{formatInTz(m.start_time, userTz)} · {m.duration_minutes}m</p>
      </button>
      {m.description && <p className="text-sm text-[#4a4a52] line-clamp-2">{m.description}</p>}
      <div className="flex items-center justify-between mt-1">
        <div className="flex items-center gap-3 text-xs text-[#82828c]">
          <Users className="w-3.5 h-3.5" />
          {m.invited_emails?.length || 0} invited
        </div>
        <div className="flex gap-2">
          <button onClick={onCopy} className="bloq-btn-secondary px-3 py-1.5 text-xs" data-testid={`copy-link-${m.meeting_id}`}>
            <Copy className="w-3 h-3 mr-1 inline" /> Copy
          </button>
          {m.status !== "canceled" && m.status !== "ended" && (
            <button onClick={onJoin} className="bloq-btn-primary px-3 py-1.5 text-xs" data-testid={`open-lobby-${m.meeting_id}`}>
              {m.status === "live" ? "Join" : "Lobby"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
